import { Injectable } from '@angular/core';
import { ApiService } from '@app/core/services/api.service';
import { Observable } from 'rxjs';
import { ICalendarItem } from "@common/models/ICalendarItem";
import { IApiStatus } from '@common/models/IApiStatus';
import { InvestmentActivity } from '@common/enums/InvestmentActivity';
import { IAlertEvent } from '@common/models/IAlertEvent';
import { CalendarItemStatus } from '@common/enums/CalendarItem';
import { ESCALATION_OVERDUE_DAYS } from '@app/modules/dashboard/dashboard.constants';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  constructor(private readonly apiService: ApiService) { }

  public addNewEvent(event: ICalendarItem): Observable<IApiStatus> {
      return this.apiService.post(`CalendarItem`, event)
  }
  public updateEvent(event: ICalendarItem): Observable<IApiStatus> {
    if(event?.tasks){
      delete event.tasks;
    }
    return this.apiService.put(`CalendarItem`, event)
  }

  public getCalendarEvents(): any {
    return this.apiService.get(`CalendarItem/tasks`)
  }

  public delete(event: ICalendarItem): Observable<IApiStatus> {
    return this.apiService.delete(`CalendarItem/${event._id}`)
  }

  public addNewTask(event: IAlertEvent, params?: { qtr: string; timezone: string }): Observable<IApiStatus> {
    const options = params ? { params } : undefined;
    return this.apiService.post(`AlertEvent`, event, options);
  }

  public deletePermanently(value: string): Observable<IApiStatus> {
    return this.apiService.delete(`AlertEvent/Value/${value}`);
  }

  saveWorkflow(body: { qtr: string, timezone: string }) {
    return this.apiService.post('CalendarItem/AddWorkflowEvents', body);
  }

  getAdminEvents(body: object): Observable<ICalendarItem[]> {
    return this.apiService.post('CalendarItem/GetWorkflowEvents', body);
  }

  publishAllEvents(ids: string[]) {
    return this.apiService.post('CalendarItem/UpdateAllEvents', ids);
  } 

  getFutureQuartersBasedonCount(count:number, currentQtr:string){
    const quarters = [];
    const [yearStr, quarterStr] = currentQtr.split('-Q');
    let year = Number(yearStr);
    let quarter = Number(quarterStr);
    for(let i=0; i<count; i++){
      quarter++;
      if(quarter === 5){
        quarter = 1;
        year++;
      }
      const v = `${year}-Q${quarter}`;
      quarters.push(v);
    }
    return quarters;
  }

  getPreviousQuartersBasedonCount(count:number, currentQtr:string){
    const quarters = [];
    const [yearStr, quarterStr] = currentQtr.split('-Q');
    let year = Number(yearStr);
    let quarter = Number(quarterStr);
    for(let i=0; i<count; i++){
      quarter--;
      if(quarter === 0){
        quarter = 4;
        year--;
      }
      const v = `${year}-Q${quarter}`;
      quarters.push(v);
    }
    return quarters;
  }

  getInvestmentTypes(enabledCount?: number): { label: string; value: string; disabled: boolean }[] {
    return Object.values(InvestmentActivity).map((type, i) => ({
      label: type,
      value: type,
      disabled: enabledCount !== undefined && i >= enabledCount
    }));
  }

  filterEventsByVisibleQuarters(events: ICalendarItem[]): ICalendarItem[] {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentQuarter = Math.ceil((now.getMonth() + 1) / 3);
    const filtered: ICalendarItem[] = [];

    // Group events by quarter
    const quarterMap: Record<string, ICalendarItem[]> = {};
    for (const event of events) {
      if (!quarterMap[event.quarter]) quarterMap[event.quarter] = [];
      quarterMap[event.quarter].push(event);
    }

    // Process each quarter group
    for (const [quarter, quarterEvents] of Object.entries(quarterMap)) {
      const [yearStr, qStr] = quarter.split('-Q');
      const year = +yearStr;
      const q = +qStr;

      //  Current quarter → include all
      if (year === currentYear && q === currentQuarter) {
        filtered.push(...quarterEvents);
        continue;
      }

      // Future quarters → include all
      if (year > currentYear || (year === currentYear && q > currentQuarter)) {
        filtered.push(...quarterEvents);
        continue;
      }
      // ⬅ Previous quarters
      const hasIncomplete = quarterEvents.some(e => e.status !== CalendarItemStatus.COMPLETE);
      if (hasIncomplete) {        
        // If any event incomplete → include all
        filtered.push(...quarterEvents);
      } else {
        // All complete — include until max endDate + 14 days, then drop the quarter
        const maxEndDate = quarterEvents.reduce((max, e) => {
          const end = new Date(e.endDate);
          return end > max ? end : max;
        }, new Date(0));
        const extendedDate = new Date(maxEndDate);
        extendedDate.setDate(extendedDate.getDate() + ESCALATION_OVERDUE_DAYS);
        if (now <= extendedDate) {
          // Include events in this quarter because we are within the 14-day extension
          filtered.push(...quarterEvents);
        }
      }
    }

    return filtered;
  }

  getCurrentQuarter(){
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const currentQuarter = Math.floor(month / 3) + 1;
    return `${year}-Q${currentQuarter}`;
  }

  public getVersionHistory(mnemonic: string, model, submodel, name): Observable<any> {
    return this.apiService.get(`VersionHistory/${mnemonic}/${model}/${submodel}/${name}?objectID=`)
  }

  updateNotifiedStatus(user){
    return this.apiService.post('NotificationActivity/Notify/' + user, {});
  }

}
